import CalendarViewMonthIcon from "@mui/icons-material/CalendarViewMonth";
import CloseIcon from "@mui/icons-material/Close";
import clsx from "clsx";
import {
	ColumnDropDividerWrapper,
	ColumnDropWrapper,
	DndDropFunction,
	DragAndColumnDropWrapper,
	DragHighlightWrapper,
	DragWrapper,
	DropPlaceholder,
	useDndStore,
} from "designSystem/dnd/index";
import dndCss from "designSystem/dnd/styles/dnd.module.css";
import { FlexColumn, FlexRowCenter, IconButton, OverflowText, TextField } from "designSystem/index";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useEffect, useRef } from "react";
import { DND_TYPE } from "../CustomViewDialogStore";
import { attributesSelectionGroupDropTypes } from "../data/dnd/data-management-tab-components";
import AttributeSelection from "./AttributesSelection";
import CustomViewAttribute from "./CustomViewAttribute";
import CustomViewAttributeGroup from "./CustomViewAttributeGroup";
import css from "./CustomViewAttributes.module.css";
import { useCustomViewAttributesStore } from "./CustomViewAttributesStore";

const attributeSelectionGroupRenderItem = (attribute: CustomViewAttribute) => <AttributeSelection attribute={attribute} />;

interface GroupContentProps {
	group: CustomViewAttributeGroup;
	isEditing: boolean;
	onBlurEditingGroup: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
	onEscapeEditingGroup: () => void;
	dropItem:  DndDropFunction<CustomViewAttribute & CustomViewAttributeGroup>;
}

const GroupContent: FunctionComponent<GroupContentProps> = observer(function GroupContent({
	group,
	isEditing,
	onBlurEditingGroup,
	onEscapeEditingGroup,
	dropItem,
}) {
	// TODO: clean this up, it should be done in the store
	const { id, name, removeGroup, attributes, flat, isMatrixGroup, isAdhocGroup, onClickAdhocGroupTitle } = group;
	const padding = flat ? 0 : 2;
	const { canDropItemAtIndex, isDragging, isOverTarget, onlyOneChildContainerIsDroppable } = useDndStore();
	const droppableGroup = !isDragging || canDropItemAtIndex(1, id);
	const isMatrixOutlineEnabled = group.isMatrixGroup && onlyOneChildContainerIsDroppable(id);

	const ref = useRef(null);
	useEffect(() => {
		if (isMatrixOutlineEnabled) {
			ref.current.scrollIntoView();
		}
	}, [isMatrixOutlineEnabled]);

	const textFieldInputProps = { className: css.groupText };

	return (
		<FlexColumn
			className={clsx(
				!flat && css.groupBorder,
				droppableGroup ? css.whiteBackground : css.greyBackground,
				isMatrixOutlineEnabled && (isOverTarget ? dndCss.activeDropZoneBorder : dndCss.inactiveDropZoneBorder),
			)}
			data-csi-selection-group={id}
			gap={1}
			pb={padding}
			position="relative"
			px={padding}
			ref={ref}
		>
			<DragWrapper
				canDrag={!flat}
				dragItem={group}
				dragType={DND_TYPE.CUSTOM_VIEW_SELECTED_GROUP}
				isFullScreen
			>
				<FlexRowCenter
					gap={2}
					justifyContent="space-between"
					p={padding}
				>
					{isMatrixGroup && <CalendarViewMonthIcon />}
					{isEditing ? (
						<TextField
							InputProps={textFieldInputProps}
							autoFocus
							borderless
							defaultValue={name}
							onBlur={onBlurEditingGroup}
							onEscape={onEscapeEditingGroup}
							placeholderRes="CustomViewCreateCustomGroupPlaceholder"
							selectOnFocus
						/>
					) : (
						!!name && (
							<OverflowText
								className={css.groupText}
								grow
								onClick={isAdhocGroup ? onClickAdhocGroupTitle : null}
								text={name}
							/>
						)
					)}
					{!flat && (
						<IconButton
							design="iconOnly"
							onClick={removeGroup}
							round
							size="small"
						>
							<CloseIcon />
						</IconButton>
					)}
				</FlexRowCenter>
			</DragWrapper>
			{attributes.length ? (
				<DragAndColumnDropWrapper<CustomViewAttribute>
					canDrag
					disableBgColorChangeOnDrag
					disableDropZoneOutline
					dragType={DND_TYPE.CUSTOM_VIEW_SELECTED_ATTRIBUTE}
					dropTypes={attributesSelectionGroupDropTypes}
					isFullScreen
					isMultiSelect
					list={attributes}
					onDrop={dropItem}
					parentId={id}
					renderItem={attributeSelectionGroupRenderItem}
				/>
			) : (
				<ColumnDropWrapper
					disableDropZoneOutline
					dropTypes={attributesSelectionGroupDropTypes}
					onDrop={dropItem}
				>
					<ColumnDropDividerWrapper parentId={id}>
						<DropPlaceholder />
					</ColumnDropDividerWrapper>
				</ColumnDropWrapper>
			)}
		</FlexColumn>
	);
});

interface AttributeSelectionGroupProps {
	/** attribute group to render */
	group: CustomViewAttributeGroup;
}

const AttributeSelectionGroup: FunctionComponent<AttributeSelectionGroupProps> = observer(function AttributeSelectionGroup({ group }) {
	const {
		dropItem,
		editingGroup,
		onBlurEditingGroup,
		onEscapeEditingGroup,
	} = useCustomViewAttributesStore();

	const isEditing = editingGroup === group;

	return group.flat ? (
		<GroupContent
		  dropItem={dropItem}
		  group={group}
		  isEditing={isEditing}
		  onBlurEditingGroup={onBlurEditingGroup}
		  onEscapeEditingGroup={onEscapeEditingGroup}
		/>
	  ) : (
		<DragHighlightWrapper>
		  <GroupContent
				dropItem={dropItem}
				group={group}
				isEditing={isEditing}
				onBlurEditingGroup={onBlurEditingGroup}
				onEscapeEditingGroup={onEscapeEditingGroup}
		  />
		</DragHighlightWrapper>
	  );
});

export default AttributeSelectionGroup;